import {useState, useRef} from 'react'
import {useParams} from 'react-router-dom'
import Countdown from 'react-countdown'
import {Space, Input} from 'antd'

import {
  Box,
  Text,
  CentsMoney,
  Button,
  ButtonLink,
  Form,
  FormItem,
  ThumbsUpCircleSecondIcon,
  ClockIcon,
} from 'common/components'
import {FIELD_RULES} from 'common/constants'
import {useSendDrawdownCode, useConfirmDrawdownCode, DrawdownItem} from '..'

interface DrawdownConfirmProps {
  projectUuid: string
  drawdown: DrawdownItem
  onClose: () => void
}

interface AmountProps {
  turquoize?: boolean
  title: string
  amount: number
}

interface ConfirmFormProps {
  message: string
  drawdownUuid: string
  onClose: () => void
}

interface InputCountdownProps {
  drawdownUuid: string
}

export default function DrawdownConfirm({
  drawdown,
  onClose,
}: DrawdownConfirmProps) {
  const {
    data: codeData,
    mutate: sendDrawdownCode,
    isMutating,
  } = useSendDrawdownCode(drawdown.uuid)

  const handleCodeSend = () => sendDrawdownCode()

  return (
    <Box align="center" pt={48}>
      <ThumbsUpCircleSecondIcon />
      <Text size="4xl" mt={12} mb={20}>
        Drawdown request approved
      </Text>
      <Text maxInterval mb={24}>
        Your drawdown request ‘Greenhite Townhouses: Project contingency
        allowances’ created 23.05.2021 and approved 24.05.2021.
      </Text>
      <Box ml={-24} mr={-24} mb={24}>
        {drawdown.requestedAmount === drawdown.approvedAmount ? (
          <Amount
            title="Requested amount – approved"
            amount={drawdown.approvedAmount}
          />
        ) : (
          <Space size={32}>
            <Amount
              turquoize
              title="Requested amount"
              amount={drawdown.requestedAmount}
            />
            <Amount title="Approved amount" amount={drawdown.approvedAmount} />
          </Space>
        )}
      </Box>
      {codeData ? (
        <ConfirmForm
          message={codeData.message}
          drawdownUuid={drawdown.uuid}
          onClose={onClose}
        />
      ) : (
        <Button onClick={handleCodeSend} loading={isMutating}>
          Confirm with TXT code
        </Button>
      )}
    </Box>
  )
}

const Amount = ({turquoize, title, amount}: AmountProps) => (
  <Box color={turquoize ? 'turquoizeDeep' : undefined} weight="medium">
    <Text size="xs" mb={2}>
      {title}, nzd
    </Text>
    <CentsMoney value={amount} size="3xl" />
  </Box>
)

const ConfirmForm = ({message, drawdownUuid, onClose}: ConfirmFormProps) => {
  const params = useParams()
  const {mutate: confirmDrawdownCode, isMutating} = useConfirmDrawdownCode(
    params.uuid,
    drawdownUuid,
    onClose
  )

  const handleFinish = ({code}: any) => confirmDrawdownCode(code)

  return (
    <Form onFinish={handleFinish}>
      <Text maxInterval mb={12}>
        {message} Please enter the code.
      </Text>
      <FormItem name="code" label="Code" rules={FIELD_RULES}>
        <Input
          autoFocus
          suffix={<InputCountdown drawdownUuid={drawdownUuid} />}
        />
      </FormItem>
      <Button block htmlType="submit" loading={isMutating}>
        Confirm drawdown
      </Button>
    </Form>
  )
}

const InputCountdown = ({drawdownUuid}: InputCountdownProps) => {
  const [countdownComplete, setCountdownComplete] = useState(false)
  const countdownRef = useRef(getCountdown())
  const {mutate: sendDrawdownCode, isMutating} = useSendDrawdownCode(
    drawdownUuid,
    () => {
      setCountdownComplete(false)
      countdownRef.current = getCountdown()
    }
  )

  if (countdownComplete)
    return (
      <ButtonLink disabled={isMutating} onClick={sendDrawdownCode}>
        resend
      </ButtonLink>
    )

  const handleComplete = () => setCountdownComplete(true)

  return (
    <Space size={4}>
      <div>
        <ClockIcon />
      </div>
      <Text size="xs" weight="medium">
        <Countdown
          date={countdownRef.current}
          renderer={({formatted}: any) => (
            <span>
              {formatted.minutes}:{formatted.seconds}
            </span>
          )}
          onComplete={handleComplete}
        />
      </Text>
    </Space>
  )
}

const getCountdown = () => Date.now() + 300_000
